"use client";
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React__default, { useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../utils/theming/useTheme.js';
import { ThemeProvider as ThemeProvider$1 } from '../utils/theming/ThemeProvider.js';
import { ThemeProvider } from 'styled-components';
import { phoneInput, select } from './styles.js';
import { useSelectEventsHandler } from './useSelectEventsHandler.web.js';
import { useInputFieldEventsHandler } from './useInputFieldEventsHandler.web.js';
import { propagateTheme, prepareCountryItems, createCountryIdentifier, destructureCountryIdentifier } from './utils.js';
import { InputField } from '../InputField/InputField.web.js';
import { useControlledPhoneInput } from './useControlledPhoneInput.web.js';

var _excluded = ["name", "id", "items", "size", "selectLabel", "color", "maxLocalPhoneNumberLength", "inputLabel", "disabled", "required", "localPhoneNumber", "dialingCode", "onPhoneNumberChange", "filterItems", "mapCountryName", "error", "errorMessage", "errorMessageProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PhoneInput = function PhoneInput(_ref) {
  var name = _ref.name,
    id = _ref.id,
    itemsProp = _ref.items,
    sizeProp = _ref.size,
    _ref$selectLabel = _ref.selectLabel,
    selectLabel = _ref$selectLabel === undefined ? 'Country' : _ref$selectLabel,
    colorProp = _ref.color,
    _ref$maxLocalPhoneNum = _ref.maxLocalPhoneNumberLength,
    maxLocalPhoneNumberLength = _ref$maxLocalPhoneNum === undefined ? 15 : _ref$maxLocalPhoneNum,
    _ref$inputLabel = _ref.inputLabel,
    inputLabel = _ref$inputLabel === undefined ? 'Phone number' : _ref$inputLabel,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    required = _ref.required,
    localPhoneNumberProp = _ref.localPhoneNumber,
    dialingCodeProp = _ref.dialingCode,
    onPhoneNumberChangeProp = _ref.onPhoneNumberChange,
    filterItems = _ref.filterItems,
    mapCountryName = _ref.mapCountryName,
    error = _ref.error,
    errorMessage = _ref.errorMessage,
    errorMessageProps = _ref.errorMessageProps,
    props = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef(null);
  var theme = useTheme();
  var modifiedTheme = propagateTheme(theme.phoneInput.web, theme);
  var size = sizeProp || theme.preferredComponentSize;
  var inputFieldEventsHandler = useInputFieldEventsHandler(disabled);
  var selectId = "".concat(id || name, "-dialing-code-select");
  var selectEventsHandler = useSelectEventsHandler(selectId);
  var countryItems = useMemo(function () {
    return prepareCountryItems(itemsProp, mapCountryName, filterItems);
  }, [itemsProp, mapCountryName, filterItems]);
  var selectItems = useMemo(function () {
    return countryItems.map(function (item) {
      return {
        name: "(+".concat(item.dialingCode, ") ").concat(item.countryName),
        value: createCountryIdentifier(item.dialingCode, item.countryCode)
      };
    });
  }, [countryItems]);
  var _useControlledPhoneIn = useControlledPhoneInput(dialingCodeProp, localPhoneNumberProp, countryItems, onPhoneNumberChangeProp),
    phoneNumber = _useControlledPhoneIn.phoneNumber,
    onChange = _useControlledPhoneIn.onChange;
  var onSelectedCountryChange = useCallback(function (ev) {
    selectEventsHandler.onSelect();
    var _destructureCountryId = destructureCountryIdentifier(ev.target.value),
      dialingCode = _destructureCountryId.dialingCode,
      countryCode = _destructureCountryId.countryCode;
    onChange(_objectSpread(_objectSpread({}, phoneNumber), {}, {
      dialingCode: dialingCode,
      countryCode: countryCode
    }));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [selectEventsHandler.onSelect, phoneNumber]);
  var handlePhoneNumberChange = useCallback(function (ev) {
    var value = ev.target.value;
    var valueWithoutPrefix = value.replace(/^\+/, '');
    var isDialingCodeIncomplete = !!(phoneNumber.dialingCode && valueWithoutPrefix.length < phoneNumber.dialingCode.length && phoneNumber.dialingCode.startsWith(valueWithoutPrefix));
    if (isDialingCodeIncomplete) {
      return;
    }
    var valueWithoutDialingCode = valueWithoutPrefix.replace(phoneNumber.dialingCode, '').trim();
    onChange(_objectSpread(_objectSpread({}, phoneNumber), {}, {
      localPhoneNumber: valueWithoutDialingCode
    }));
  }, [phoneNumber, onChange]);
  var positionCursorAtEnd = function positionCursorAtEnd() {
    var el = inputRef.current;
    //istanbul ignore next line
    if (!el) return;
    var length = el.value.length;
    el.setSelectionRange(length, length);
  };
  var color = colorProp || theme.phoneInput.web.color;
  var inputMaxLength = useMemo(function () {
    return phoneNumber.dialingCode.length + maxLocalPhoneNumberLength + 2;
  }, [phoneNumber.dialingCode.length, maxLocalPhoneNumberLength]);
  return /*#__PURE__*/React__default.createElement(ThemeProvider, {
    theme: modifiedTheme
  }, /*#__PURE__*/React__default.createElement(ThemeProvider$1, {
    theme: modifiedTheme
  }, /*#__PURE__*/React__default.createElement(phoneInput, _extends({
    "data-testid": "elemental-PhoneInput-".concat(id || name),
    $color: color
  }, props), /*#__PURE__*/React__default.createElement(select, {
    $error: !!error,
    disabled: disabled,
    $inputFieldHovered: inputFieldEventsHandler.hoverCaptured,
    $inputFieldFocused: inputFieldEventsHandler.focusCaptured,
    $opened: selectEventsHandler.selectOpened && countryItems.length > 0,
    onClick: selectEventsHandler.onClick,
    onKeyDown: selectEventsHandler.onKeyDown,
    name: name,
    id: selectId,
    label: selectLabel,
    placeholder: "",
    items: selectItems,
    selected: createCountryIdentifier(phoneNumber.dialingCode, phoneNumber.countryCode),
    onChange: onSelectedCountryChange,
    displayValue: phoneNumber.countryCode,
    size: size,
    width: modifiedTheme.phoneInput.web.sizings[size].heading.width,
    minWidth: modifiedTheme.phoneInput.web.sizings[size].heading.width,
    color: color
  }), /*#__PURE__*/React__default.createElement(InputField, {
    errorMessageProps: _objectSpread({
      marginLeft: "-".concat(modifiedTheme.phoneInput.web.sizings[size].heading.width)
    }, errorMessageProps),
    error: error,
    errorMessage: errorMessage,
    required: required,
    inputRef: inputRef,
    disabled: disabled,
    maxLength: inputMaxLength,
    onFocusCapture: inputFieldEventsHandler.onFocusCapture,
    onFocus: positionCursorAtEnd,
    onBlurCapture: inputFieldEventsHandler.onBlurCapture,
    onMouseEnter: inputFieldEventsHandler.onMouseEnter,
    onMouseLeave: inputFieldEventsHandler.onMouseLeave,
    name: name,
    id: id,
    type: "tel",
    size: size,
    label: inputLabel,
    value: "+".concat(phoneNumber.dialingCode, " ").concat(phoneNumber.localPhoneNumber),
    "data-testid": "elemental-PhoneInput-".concat(id || name, "-input"),
    onChange: handlePhoneNumberChange,
    color: color
  }))));
};

export { PhoneInput };
